import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { Container, Button, Form, Accordion, Card, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as XLSX from 'xlsx';

const CalculadoraIncrementos = () => {
  const [juegos, setJuegos] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [juegoSeleccionado, setJuegoSeleccionado] = useState('');
  const [periodosSeleccionados, setPeriodosSeleccionados] = useState([]);
  const [cantidadGrupos, setCantidadGrupos] = useState(4);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState('0');
  const [datos, setDatos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [grupos, setGrupos] = useState([]);

  // Parámetros configurables
  const [objetivoTipo, setObjetivoTipo] = useState('porcentaje'); // 'porcentaje' o 'monto'
  const [objetivoValor, setObjetivoValor] = useState(10); // % o monto
  const [comision, setComision] = useState(18); // %
  const [premios, setPremios] = useState(77); // %
  const [cumplen, setCumplen] = useState(20); // % global
  const [cumplimientoPorFila, setCumplimientoPorFila] = useState({}); // {id: %cumplimiento}
  const [margen, setMargen] = useState(1); // %
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    axios.get('https://us-central1-inprojuy-f1173.cloudfunctions.net/app/api/estadisticas/juegos')
      .then(res => {
        setJuegos(res.data.juegos || []);
      })
      .catch(err => console.error('Error al cargar juegos:', err));
  }, []);

  useEffect(() => {
    axios.get('https://us-central1-inprojuy-f1173.cloudfunctions.net/app/api/estadisticas/periodos-cargados')
      .then(res => setPeriodos(res.data.periodos))
      .catch(err => {});
  }, []);

  const handleJuegoChange = (e) => setJuegoSeleccionado(e.target.value);

  const handlePeriodoChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    setPeriodosSeleccionados(selected);
  };

  const handleCantidadGruposChange = (e) => {
    const value = parseInt(e.target.value);
    setCantidadGrupos(value);
  };

  const handleGrupoChange = (e) => {
    const index = parseInt(e.target.value);
    setGrupoSeleccionado(e.target.value);
    if (grupos[index]) {
      setDatosFiltrados(grupos[index]);
    }
  };

  const fetchDatos = () => {
    if (!juegoSeleccionado || periodosSeleccionados.length === 0) return;

    setLoading(true);
    const params = new URLSearchParams();
    params.append('juego', juegoSeleccionado);
    periodosSeleccionados.forEach(p => params.append('periodos', p));

    axios.get(`https://us-central1-inprojuy-f1173.cloudfunctions.net/app/api/estadisticas/resumen-por-agencia-juego?${params.toString()}`)
      .then((res) => {
        const detalle = res.data.detalle || [];
        const datosOrdenados = detalle
          .sort((a, b) => b.promedio - a.promedio)
          .map((item, index) => ({
            id: index + 1,
            agencia: item.agencia,
            total: item.total,
            ...item.totalPorPeriodo,
            promedio: item.promedio
          }));

        const total = datosOrdenados.length;
        const tamañoGrupo = Math.ceil(total / cantidadGrupos);
        const nuevosGrupos = Array.from({ length: cantidadGrupos }, (_, i) =>
          datosOrdenados.slice(i * tamañoGrupo, (i + 1) * tamañoGrupo)
        );

        setDatos(datosOrdenados);
        setGrupos(nuevosGrupos);
        setGrupoSeleccionado('0');
        setDatosFiltrados(nuevosGrupos[0]);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error al cargar resumen por agencia y juego:', err);
        setLoading(false);
      });
  };

  // Recalcular solo cálculos locales (sin GET)
  const recalcular = () => {
    if (!datos.length) return;
    const total = datos.length;
    const tamañoGrupo = Math.ceil(total / cantidadGrupos);
    const nuevosGrupos = Array.from({ length: cantidadGrupos }, (_, i) =>
      datos.slice(i * tamañoGrupo, (i + 1) * tamañoGrupo)
    );
    setGrupos(nuevosGrupos);
    setGrupoSeleccionado('0');
    setDatosFiltrados(nuevosGrupos[0]);
  };


  // Cálculos por agencia
  const [totalMargenNetoLoteria, setTotalMargenNetoLoteria] = useState(0);
  // Para resumen visual
  const [resumenGrupo, setResumenGrupo] = useState({ margenTotal: 0, margenNetoPremios: 0, remanente: 0, margenMinimo: 0 });
  const datosCalculados = useMemo(() => {
    // Agrupar por grupo (en este contexto, datosFiltrados es el grupo actual)
    const rows = datosFiltrados.map((row) => {
      const totales = periodosSeleccionados.map(p => Number(row[p] || 0));
      const max_total = Math.max(...totales);
      let venta_extra = 0;
      if (objetivoTipo === 'porcentaje') {
        // Porcentaje: objetivo es un % extra sobre el máximo de cada agencia
        venta_extra = max_total * (Number(objetivoValor) / 100);
      } else {
        // Monto fijo: objetivo es alcanzar el monto objetivo, no un extra
        // Si la agencia ya supera el objetivo, venta_extra = 0 (no necesita vender más)
        // Si no, venta_extra = objetivo - max_total (lo que le falta para llegar)
        venta_extra = Math.max(0, Number(objetivoValor) - max_total);
      }
      const comision_agencia = venta_extra * (Number(comision) / 100);
      const premios_pagar = venta_extra * (Number(premios) / 100);
      const margen_extra = venta_extra - comision_agencia - premios_pagar;
      // margen_minimo siempre es venta_extra * (margen / 100)
      const margen_minimo = venta_extra * (Number(margen) / 100);
      let cumplimientoFila = cumplimientoPorFila[row.id];
      if (cumplimientoFila === undefined || cumplimientoFila === "") cumplimientoFila = Number(cumplen);
      cumplimientoFila = Number(cumplimientoFila);
      // Margen neto proporcional al % de cumplimiento
      const margen_neto = margen_extra * (cumplimientoFila / 100);
      return {
        ...row,
        max_total,
        cumplimientoFila,
        venta_extra,
        comision_agencia,
        premios_pagar,
        margen_extra,
        margen_minimo,
        margen_neto,
      };
    });

    // Cálculos por grupo (solo sobre datosFiltrados)
    const totalMargenNeto = rows.reduce((acc, r) => acc + r.margen_neto, 0);
    const totalMargenMinimo = rows.reduce((acc, r) => acc + r.margen_minimo, 0);
    const totalPremios = totalMargenNeto - totalMargenMinimo;
    const totalMargenNeto100 = rows.filter(r => r.cumplimientoFila === 100).reduce((acc, r) => acc + r.margen_neto, 0);
    const rowsFinal = rows.map(r => {
      if (r.cumplimientoFila === 100 && totalMargenNeto100 > 0 && totalPremios > 0) {
        const proporcion = r.margen_neto / totalMargenNeto100;
        const premio_max = totalPremios * proporcion;
        return { ...r, premio_max };
      } else {
        return { ...r, premio_max: 0 };
      }
    });
    // El total de margen neto se actualiza por useEffect
    setTimeout(() => setTotalMargenNetoLoteria(totalMargenNeto), 0);
    // Resumen visual
    const remanente = rowsFinal.filter(r => r.cumplimientoFila < 100).reduce((acc, r) => acc + (r.margen_neto || 0), 0);
    const margenNeto100 = rowsFinal.filter(r => r.cumplimientoFila === 100).reduce((acc, r) => acc + (r.margen_neto || 0), 0);
    const margenMinimo = rowsFinal.reduce((acc, r) => acc + (r.margen_minimo || 0), 0);
    setTimeout(() => setResumenGrupo({ margenTotal: totalMargenNeto, margenNetoPremios: margenNeto100, remanente, margenMinimo }), 0);
    return rowsFinal;
  }, [datosFiltrados, periodosSeleccionados, objetivoTipo, objetivoValor, comision, premios, cumplen, margen, cumplimientoPorFila]);

  // Helper to get max period value for cell highlighting
  const getMaxPeriodo = (row) => {
    const periodVals = periodosSeleccionados.map(p => Number(row[p] || 0));
    return Math.max(...periodVals);
  };

  // Columnas principales y avanzadas
  const columnasPrincipales = [
    { field: 'agencia', headerName: 'Agencia', width: 120, pinned: 'left' },
    { field: 'max_total', headerName: 'Max. Período', width: 130, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
    { field: 'venta_extra', headerName: 'Objetivo Venta Extra', width: 140, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
    {
      field: 'cumplimientoFila',
      headerName: '% Cumplimiento',
      width: 120,
      type: 'number',
      pinned: 'left',
      editable: true,
      renderCell: (params) => {
        let value = cumplimientoPorFila[params.row.id];
        if (value === undefined) value = params.value;
        const handleChange = (e) => {
          let val = e.target.value;
          if (val === '') val = '';
          else val = Math.max(0, Math.min(100, Number(val)));
          setCumplimientoPorFila(prev => ({ ...prev, [params.row.id]: val }));
        };
        const handleBlur = (e) => {
          let val = e.target.value;
          if (val === '' || isNaN(Number(val))) val = 0;
          else val = Math.max(0, Math.min(100, Number(val)));
          setCumplimientoPorFila(prev => ({ ...prev, [params.row.id]: val }));
        };
        const handleKeyDown = (e) => {
          if (e.key === 'Enter') {
            let val = e.target.value;
            if (val === '' || isNaN(Number(val))) val = 0;
            else val = Math.max(0, Math.min(100, Number(val)));
            setCumplimientoPorFila(prev => ({ ...prev, [params.row.id]: val }));
            e.target.blur();
          }
        };
        return (
          <input
            type="number"
            min={0}
            max={100}
            value={value}
            style={{ width: 60 }}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        );
      },
    },
    { field: 'margen_extra', headerName: 'Margen Extra', width: 120, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
    {
      field: 'premio_max',
      headerName: 'Premio Máx. Auto.',
      width: 150,
      type: 'number',
      valueFormatter: ({ value }) => value?.toLocaleString('es-AR'),
      cellClassName: (params) => Number(params.value) > 0 ? 'premio-max-cell' : '',
    },
    { field: 'margen_neto', headerName: 'Margen Neto', width: 120, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
  ];

  // Columnas avanzadas (detalles)
  const columnasAvanzadas = [
    { field: 'total', headerName: 'Total Períodos', width: 130, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
    ...periodosSeleccionados.map(p => ({
      field: p,
      headerName: `Total ${p}`,
      width: 120,
      type: 'number',
      valueFormatter: ({ value }) => value?.toLocaleString('es-AR'),
      cellClassName: (params) => {
        const maxVal = getMaxPeriodo(params.row);
        return Number(params.value) === maxVal ? 'max-period-cell' : '';
      }
    })),
    { field: 'promedio', headerName: 'Promedio', width: 120, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
    { field: 'comision_agencia', headerName: 'Comisión', width: 110, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
    { field: 'premios_pagar', headerName: 'Premios', width: 110, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
    // FIX: Mostrar solo margen_minimo calculado como venta_extra * (margen / 100)
    { field: 'margen_minimo', headerName: 'Margen Mínimo', width: 120, type: 'number', valueFormatter: ({ value }) => value?.toLocaleString('es-AR') },
  ];

  // Estado para mostrar/ocultar detalles
  const [verDetalles, setVerDetalles] = useState(false);
  const columns = verDetalles ? [...columnasPrincipales, ...columnasAvanzadas] : columnasPrincipales;

  // Exportar solo columnas principales o seleccionadas
  const exportToExcel = () => {
    const cols = columnasPrincipales.map(c => c.field);
    const data = datosCalculados.map(row => {
      const obj = {};
      cols.forEach(f => obj[f] = row[f]);
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ResumenPorAgenciaJuego');
    XLSX.writeFile(wb, 'resumen_por_agencia_juego.xlsx');
  };


  // --- Resumen de utilidades finales del grupo seleccionado ---
  // Usar los datos ya calculados (con cumplimiento actualizado) para los totales
  const grupoActual = datosCalculados;
  const sumatoriaVentaExtra = grupoActual.reduce((acc, r) => acc + (r.venta_extra || 0), 0);
  const sumatoriaComisiones = grupoActual.reduce((acc, r) => acc + (r.comision_agencia || 0), 0);
  const sumatoriaPremios = grupoActual.reduce((acc, r) => acc + (r.premios_pagar || 0), 0);
  const sumatoriaPremiosObjetivo = grupoActual.reduce((acc, r) => acc + (r.premio_max || 0), 0);
  const sumatoriaMargenLoteria = sumatoriaVentaExtra - sumatoriaComisiones - sumatoriaPremios - sumatoriaPremiosObjetivo;

  const formatoMoneda = (valor) => valor.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 });

  return (
    <div style={{ background: '#f7f7f7', minHeight: '100vh', width: '100%' }}>
      <Container fluid style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 16px 24px 16px' }}>
        <h2 className="text-center mb-4" style={{ fontWeight: 700, letterSpacing: 0.5 }}>Calculadora de Incrementos</h2>

        {/* Summary Cards Row */}
        <Row className="g-3 mb-4" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
          <Col lg={2} md={4} sm={6} xs={12}>
            <Card className="h-100 shadow" style={{ background: '#03a9f4', color: 'white', minHeight: 120, borderRadius: 16 }}>
              <Card.Body className="d-flex flex-column justify-content-center align-items-center">
                <Card.Title style={{ fontSize: 18, fontWeight: 600, textAlign: 'center' }}>Recaudación Extra Total</Card.Title>
                <Card.Text style={{ fontSize: 20, fontWeight: 'bold', marginTop: 8, color: '#111', textAlign: 'center' }}>
                  {formatoMoneda(sumatoriaVentaExtra)}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} sm={6} xs={12}>
            <Card className="h-100 shadow" style={{ background: '#1976d2', color: 'white', minHeight: 120, borderRadius: 16 }}>
              <Card.Body className="d-flex flex-column justify-content-center align-items-center">
                <Card.Title style={{ fontSize: 18, fontWeight: 600, textAlign: 'center' }}>Comisiones Total</Card.Title>
                <Card.Text style={{ fontSize: 20, fontWeight: 'bold', marginTop: 8, color: '#111', textAlign: 'center' }}>
                  {formatoMoneda(sumatoriaComisiones)}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} sm={6} xs={12}>
            <Card className="h-100 shadow" style={{ background: '#e53935', color: 'white', minHeight: 120, borderRadius: 16 }}>
              <Card.Body className="d-flex flex-column justify-content-center align-items-center">
                <Card.Title style={{ fontSize: 18, fontWeight: 600, textAlign: 'center' }}>Premios a Pagar Total</Card.Title>
                <Card.Text style={{ fontSize: 20, fontWeight: 'bold', marginTop: 8, color: '#111', textAlign: 'center' }}>
                  {formatoMoneda(sumatoriaPremios)}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} sm={6} xs={12}>
            <Card className="h-100 shadow" style={{ background: '#ff9800', color: 'white', minHeight: 120, borderRadius: 16 }}>
              <Card.Body className="d-flex flex-column justify-content-center align-items-center">
                <Card.Title style={{ fontSize: 18, fontWeight: 600, textAlign: 'center' }}>Premios por Objetivo</Card.Title>
                {/* Mostrar porcentaje sobre la recaudación extra total */}
                <div style={{ fontSize: 13, color: '#fff', fontWeight: 500, marginBottom: 2, textAlign: 'center' }}>
                  {sumatoriaVentaExtra > 0
                    ? `${((sumatoriaPremiosObjetivo / sumatoriaVentaExtra) * 100).toFixed(2)}% de la recaudación extra`
                    : '—'}
                </div>
                <Card.Text style={{ fontSize: 20, fontWeight: 'bold', marginTop: 2, color: '#111', textAlign: 'center' }}>
                  {formatoMoneda(sumatoriaPremiosObjetivo)}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4} md={8} sm={12} xs={12}>
            <Card
              className="h-100 shadow"
              style={{
                background: sumatoriaMargenLoteria < 0 ? '#b71c1c' : '#43a047',
                color: 'white',
                minHeight: 120,
                border: sumatoriaMargenLoteria < 0 ? '3px solid #ff5252' : '3px solid #43a047',
                boxShadow: sumatoriaMargenLoteria < 0 ? '0 0 16px #ff5252' : '0 0 16px #43a047',
                borderRadius: 16,
              }}
            >
              <Card.Body className="d-flex flex-column justify-content-center align-items-center">
                <Card.Title style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.5, textAlign: 'center' }}>Margen Neto Lotería</Card.Title>
                {/* Mostrar porcentaje sobre la recaudación extra total */}
                <div style={{ fontSize: 14, color: '#fff', fontWeight: 500, marginBottom: 2, textAlign: 'center' }}>
                  {sumatoriaVentaExtra > 0
                    ? `${((sumatoriaMargenLoteria / sumatoriaVentaExtra) * 100).toFixed(2)}% de la recaudación extra`
                    : '—'}
                </div>
                <Card.Text style={{ fontSize: 26, fontWeight: 'bold', marginTop: 2, color: sumatoriaMargenLoteria < 0 ? '#ffeb3b' : 'white', textAlign: 'center' }}>
                  {formatoMoneda(sumatoriaMargenLoteria)}
                </Card.Text>
                {sumatoriaMargenLoteria < 0 && (
                  <div style={{ color: '#ffeb3b', fontWeight: 600, fontSize: 16, marginTop: 4, textAlign: 'center' }}>¡Margen negativo!</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Simulation Parameters Accordion */}
        <Row className="mb-3" style={{ justifyContent: 'center' }}>
          <Col lg={10} md={12} sm={12} xs={12}>
            <Accordion defaultActiveKey="0" className="mb-3" style={{ borderRadius: 12 }}>
              <Accordion.Item eventKey="0">
                <Accordion.Header>Parámetros de simulación</Accordion.Header>
                <Accordion.Body>
                  <Row className="align-items-end g-3">
                    <Col md={2} xs={6}>
                      <Form.Group>
                        <Form.Label>
                          Objetivo de incremento
                          <OverlayTrigger placement="top" overlay={<Tooltip>Porcentaje o monto de incremento sobre el máximo periodo.</Tooltip>}>
                            <InfoOutlinedIcon fontSize="small" style={{ marginLeft: 4, color: '#1976d2' }} />
                          </OverlayTrigger>
                        </Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Control
                            type="number"
                            min="0"
                            value={objetivoValor}
                            onChange={e => setObjetivoValor(e.target.value)}
                            style={{ width: 140, fontWeight: 600, fontSize: 18 }}
                          />
                          <Form.Select value={objetivoTipo} onChange={e => setObjetivoTipo(e.target.value)} style={{ width: 90, fontWeight: 600, fontSize: 16 }}>
                            <option value="porcentaje">%</option>
                            <option value="monto">$</option>
                          </Form.Select>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={2} xs={6}>
                      <Form.Group>
                        <Form.Label>
                          % Comisión agencia
                          <OverlayTrigger placement="top" overlay={<Tooltip>Porcentaje de comisión que recibe la agencia.</Tooltip>}>
                            <InfoOutlinedIcon fontSize="small" style={{ marginLeft: 4, color: '#1976d2' }} />
                          </OverlayTrigger>
                        </Form.Label>
                        <Form.Control type="number" min="0" max="100" value={comision} onChange={e => setComision(e.target.value)} style={{ width: 80 }} />
                      </Form.Group>
                    </Col>
                    <Col md={2} xs={6}>
                      <Form.Group>
                        <Form.Label>
                          % Destinado a premios
                          <OverlayTrigger placement="top" overlay={<Tooltip>Porcentaje de la venta extra destinado a premios.</Tooltip>}>
                            <InfoOutlinedIcon fontSize="small" style={{ marginLeft: 4, color: '#1976d2' }} />
                          </OverlayTrigger>
                        </Form.Label>
                        <Form.Control type="number" min="0" max="100" value={premios} onChange={e => setPremios(e.target.value)} style={{ width: 80 }} />
                      </Form.Group>
                    </Col>
                    <Col md={2} xs={6}>
                      <Form.Group>
                        <Form.Label>
                          % Agencias que cumplen
                          <OverlayTrigger placement="top" overlay={<Tooltip>Porcentaje de agencias que cumplen el objetivo.</Tooltip>}>
                            <InfoOutlinedIcon fontSize="small" style={{ marginLeft: 4, color: '#1976d2' }} />
                          </OverlayTrigger>
                        </Form.Label>
                        <Form.Control type="number" min="0" max="100" value={cumplen} onChange={e => setCumplen(e.target.value)} style={{ width: 80 }} />
                      </Form.Group>
                    </Col>
                    <Col md={2} xs={6}>
                      <Form.Group>
                        <Form.Label>
                          % Margen mínimo lotería
                          <OverlayTrigger placement="top" overlay={<Tooltip>Porcentaje de margen mínimo que retiene la lotería.</Tooltip>}>
                            <InfoOutlinedIcon fontSize="small" style={{ marginLeft: 4, color: '#1976d2' }} />
                          </OverlayTrigger>
                        </Form.Label>
                        <Form.Control type="number" min="0" max="100" value={margen} onChange={e => setMargen(e.target.value)} style={{ width: 80 }} />
                      </Form.Group>
                    </Col>
                    <Col md={2} xs={12} className="text-end">
                      <Button onClick={fetchDatos} variant="primary" className="mb-2 w-100">Consultar</Button>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>
        </Row>

        {/* Filter Controls */}
        <Row className="mb-3 g-3" style={{ justifyContent: 'center' }}>
          <Col md={4} sm={6} xs={12}>
            <Form.Group controlId="juegoSelect">
              <Form.Label>Seleccionar Juego</Form.Label>
              <Form.Select value={juegoSeleccionado} onChange={handleJuegoChange}>
                <option value="">-- Seleccione un juego --</option>
                {juegos.map(j => (
                  <option key={j} value={j}>{j}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4} sm={6} xs={12}>
            <Form.Group controlId="periodosSelect">
              <Form.Label>Seleccionar Períodos</Form.Label>
              <Form.Control as="select" multiple onChange={handlePeriodoChange} value={periodosSeleccionados}>
                {periodos.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={4} sm={6} xs={12}>
            <Form.Group controlId="cantidadGruposSelect">
              <Form.Label>Cantidad de Grupos</Form.Label>
              <Form.Select value={cantidadGrupos} onChange={handleCantidadGruposChange}>
                {[1, 2, 3, 4].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Grupo y acciones */}
        {grupos.length > 0 && (
          <Row className="mb-3 g-3" style={{ justifyContent: 'center', alignItems: 'end' }}>
            <Col md={4} sm={6} xs={12}>
              <Form.Group controlId="grupoSelect">
                <Form.Label>Ver Grupo</Form.Label>
                <Form.Select value={grupoSeleccionado} onChange={handleGrupoChange}>
                  {grupos.map((_, i) => (
                    <option key={i} value={i}>Grupo {i + 1}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={8} sm={6} xs={12} className="d-flex gap-2 justify-content-end align-items-end">
              <Button onClick={recalcular} variant="warning" disabled={!datos.length}>
                Recalcular
              </Button>
              <Button onClick={exportToExcel} variant="success" disabled={datosFiltrados.length === 0}>
                Exportar a Excel
              </Button>
              <Button variant="outline-secondary" onClick={() => setVerDetalles(v => !v)}>
                {verDetalles ? 'Ocultar detalles' : 'Ver más detalles'}
              </Button>
            </Col>
          </Row>
        )}

        {/* DataGrid Table */}
        <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: 16, marginBottom: 32 }}>
          <DataGrid
            rows={datosCalculados}
            columns={columns}
            loading={loading}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 20, 50]}
            pagination
            autoHeight
            getRowClassName={params => params.row.cumplimientoFila === 100 ? 'MuiDataGrid-row MuiDataGrid-row--destacado' : ''}
            sx={{
              '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                zIndex: 2,
              },
              '& .MuiDataGrid-row--destacado': {
                backgroundColor: '#e3fcec !important',
              },
              '& .max-period-cell': {
                backgroundColor: '#ffe082 !important',
                fontWeight: 'bold',
              },
              '& .premio-max-cell': {
                backgroundColor: '#ffe0b2 !important',
                color: '#d84315 !important',
                fontWeight: 'bold',
              },
            }}
          />
        </div>
      </Container>
    </div>
  );
};

export default CalculadoraIncrementos;